import React from 'react';
import { 
  Square, Home, Car, 
  Calendar, TreePine, Tag,
  Maximize2, Minimize2, Ruler,
  Bed, Bath, Layout,
  Shield, Droplet, Zap,
  Wifi, CheckCircle, XCircle
} from 'lucide-react';

const CharacteristicsSection = ({ property, showAll, toggleShowAll }) => {
  // Procesamiento seguro de datos
  const getPropertyValue = (value, defaultValue = '--') => {
    return value !== undefined && value !== null ? value : defaultValue;
  };

  const checkService = (serviceName) => {
    if (!property.servicios_basicos) return false;
    
    // Manejar tanto arrays como strings separados por comas
    if (Array.isArray(property.servicios_basicos)) {
      return property.servicios_basicos.includes(serviceName);
    } else if (typeof property.servicios_basicos === 'string') {
      return property.servicios_basicos.split(',').map(s => s.trim()).includes(serviceName);
    }
    return false;
  };

  // Componente de Tarjeta de Características
  const FeatureCard = ({ icon: Icon, title, values, color = "blue" }) => {
    // Mapeo de colores adaptativos
    const colorConfig = {
      blue: { 
        bg: "backdrop-blur-xl bg-blue-50/80 dark:bg-blue-500/20", 
        text: "text-blue-600 dark:text-blue-400", 
        icon: "text-blue-500 dark:text-blue-400", 
        border: "border-blue-100 dark:border-blue-500/30",
        iconBg: "bg-white dark:bg-white/10"
      },
      emerald: { 
        bg: "backdrop-blur-xl bg-emerald-50/80 dark:bg-emerald-500/20", 
        text: "text-emerald-600 dark:text-emerald-400", 
        icon: "text-emerald-500 dark:text-emerald-400", 
        border: "border-emerald-100 dark:border-emerald-500/30",
        iconBg: "bg-white dark:bg-white/10"
      },
      amber: { 
        bg: "backdrop-blur-xl bg-amber-50/80 dark:bg-amber-500/20", 
        text: "text-amber-600 dark:text-amber-400", 
        icon: "text-amber-500 dark:text-amber-400", 
        border: "border-amber-100 dark:border-amber-500/30",
        iconBg: "bg-white dark:bg-white/10"
      },
      purple: { 
        bg: "backdrop-blur-xl bg-purple-50/80 dark:bg-purple-500/20", 
        text: "text-purple-600 dark:text-purple-400", 
        icon: "text-purple-500 dark:text-purple-400", 
        border: "border-purple-100 dark:border-purple-500/30",
        iconBg: "bg-white dark:bg-white/10"
      },
      rose: { 
        bg: "backdrop-blur-xl bg-rose-50/80 dark:bg-rose-500/20", 
        text: "text-rose-600 dark:text-rose-400", 
        icon: "text-rose-500 dark:text-rose-400", 
        border: "border-rose-100 dark:border-rose-500/30",
        iconBg: "bg-white dark:bg-white/10"
      },
      gray: { 
        bg: "backdrop-blur-xl bg-gray-50/80 dark:bg-white/5", 
        text: "text-gray-600 dark:text-gray-400", 
        icon: "text-gray-500 dark:text-gray-400", 
        border: "border-gray-100 dark:border-white/20",
        iconBg: "bg-white dark:bg-white/10"
      }
    };
    
    const colors = colorConfig[color] || colorConfig.blue;
    
    return (
      <div className={`${colors.bg} rounded-xl p-5 border ${colors.border} shadow-sm dark:shadow-black/20
                      transition-all duration-300 hover:shadow-md dark:hover:shadow-black/40 h-full`}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-lg ${colors.iconBg}`}>
            <Icon className={`w-5 h-5 ${colors.icon}`} />
          </div>
          <h3 className={`text-lg font-bold ${colors.text}`}>{title}</h3>
        </div>
        
        <div className="space-y-3">
          {values.map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400 font-medium">{item.label}:</span>
              <span className={`${item.highlight ? 'font-semibold ' + colors.text : 'text-gray-700 dark:text-gray-300'}`}>
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Componente de Característica Booleana
  const BooleanFeature = ({ title, isAvailable, icon: Icon, color = "blue" }) => {
    const colorConfig = {
      blue: { 
        bg: "backdrop-blur-xl bg-blue-50/80 dark:bg-blue-500/20", 
        text: "text-blue-600 dark:text-blue-400", 
        icon: "text-blue-500 dark:text-blue-400",
        border: "border-blue-100 dark:border-blue-500/30"
      },
      emerald: { 
        bg: "backdrop-blur-xl bg-emerald-50/80 dark:bg-emerald-500/20", 
        text: "text-emerald-600 dark:text-emerald-400", 
        icon: "text-emerald-500 dark:text-emerald-400",
        border: "border-emerald-100 dark:border-emerald-500/30"
      },
      amber: { 
        bg: "backdrop-blur-xl bg-amber-50/80 dark:bg-amber-500/20", 
        text: "text-amber-600 dark:text-amber-400", 
        icon: "text-amber-500 dark:text-amber-400",
        border: "border-amber-100 dark:border-amber-500/30"
      },
      gray: { 
        bg: "backdrop-blur-xl bg-gray-50/80 dark:bg-white/5", 
        text: "text-gray-600 dark:text-gray-400", 
        icon: "text-gray-500 dark:text-gray-400",
        border: "border-gray-100 dark:border-white/20"
      }
    };
    
    const colors = colorConfig[color] || colorConfig.blue;
    
    return (
      <div className={`flex items-center justify-between p-4 rounded-xl ${colors.bg} shadow-sm dark:shadow-black/20 border ${colors.border}
                      transition-all duration-300 hover:shadow-md dark:hover:shadow-black/40`}>
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${colors.icon}`} />
          <span className={`font-medium ${colors.text}`}>{title}</span>
        </div>
        
        {isAvailable ? (
          <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />
        ) : (
          <XCircle className="w-5 h-5 text-gray-400 dark:text-gray-500" />
        )}
      </div>
    );
  };

  // Componente InfoCard para información destacada
  const InfoCard = ({ icon: Icon, title, value, color = "blue" }) => {
    const colorConfig = {
      blue: { 
        bg: "backdrop-blur-xl bg-blue-50/80 dark:bg-blue-500/20", 
        text: "text-blue-700 dark:text-blue-300", 
        icon: "text-blue-500 dark:text-blue-400", 
        value: "text-blue-800 dark:text-blue-200",
        border: "border-blue-100 dark:border-blue-500/30"
      },
      emerald: { 
        bg: "backdrop-blur-xl bg-emerald-50/80 dark:bg-emerald-500/20", 
        text: "text-emerald-700 dark:text-emerald-300", 
        icon: "text-emerald-500 dark:text-emerald-400", 
        value: "text-emerald-800 dark:text-emerald-200",
        border: "border-emerald-100 dark:border-emerald-500/30"
      },
      amber: { 
        bg: "backdrop-blur-xl bg-amber-50/80 dark:bg-amber-500/20", 
        text: "text-amber-700 dark:text-amber-300", 
        icon: "text-amber-500 dark:text-amber-400", 
        value: "text-amber-800 dark:text-amber-200",
        border: "border-amber-100 dark:border-amber-500/30"
      },
      gray: { 
        bg: "backdrop-blur-xl bg-gray-50/80 dark:bg-white/5", 
        text: "text-gray-700 dark:text-gray-300", 
        icon: "text-gray-500 dark:text-gray-400", 
        value: "text-gray-800 dark:text-gray-200",
        border: "border-gray-100 dark:border-white/20"
      }
    };
    
    const colors = colorConfig[color] || colorConfig.blue;
    
    return (
      <div className={`${colors.bg} rounded-xl p-5 shadow-sm dark:shadow-black/20 border ${colors.border}`}>
        <div className="flex items-center gap-3 mb-3">
          <Icon className={`w-5 h-5 ${colors.icon}`} />
          <h4 className={`font-semibold ${colors.text}`}>{title}</h4>
        </div>
        <p className={`text-2xl font-bold ${colors.value}`}>{value}</p>
      </div>
    );
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="relative mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-0.5 w-16 bg-gradient-to-r from-transparent to-blue-500 dark:to-blue-400 rounded-full"></div>
            <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 dark:from-blue-400 dark:to-blue-500 rounded-2xl shadow-lg dark:shadow-black/30">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div className="h-0.5 w-16 bg-gradient-to-l from-transparent to-blue-500 dark:to-blue-400 rounded-full"></div>
          </div>
          
          <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-blue-700 to-purple-600 dark:from-blue-400 dark:via-blue-500 dark:to-purple-400
                         bg-clip-text text-transparent mb-3">
            {showAll ? 'Características Completas' : 'Características Principales'}
          </h2>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-blue-400 dark:bg-blue-300 rounded-full animate-pulse"></div>
            <div className="w-16 h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 dark:from-blue-300 dark:to-purple-300 rounded-full"></div>
            <div className="w-2 h-2 bg-purple-400 dark:bg-purple-300 rounded-full animate-pulse delay-75"></div>
          </div>
        </div>
        
        <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
        </p>
      </div>

      {/* Main Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Área Card */}
        <FeatureCard 
          icon={Square} 
          title="ÁREA DEL INMUEBLE"
          color="blue"
          values={[
            { 
              label: "Área Ocupada", 
              value: `${getPropertyValue(property.area_ocupada)} m²`, 
              highlight: true 
            },
            { 
              label: "Área Total", 
              value: `${getPropertyValue(property.area_total)} m²`, 
              highlight: true 
            }
          ]}
        />

        {/* House Card */}
        <FeatureCard 
          icon={Home} 
          title="VIVIENDA COMPLETA"
          color="emerald"
          values={[
            { 
              label: "Habitaciones", 
              value: getPropertyValue(property.habitaciones), 
              highlight: true 
            },
            { 
              label: "Baños", 
              value: getPropertyValue(property.banos), 
              highlight: true 
            }
          ]}
        />

        {/* Price Card */}
        <FeatureCard 
          icon={Tag} 
          title="PRECIO Y OPERACIÓN"
          color="amber"
          values={[
            { 
              label: "Operación", 
              value: property.operacion === 'alquiler' ? 'Alquiler' : 'Venta', 
              highlight: true 
            },
            { 
              label: "Antigüedad", 
              value: `${getPropertyValue(property.antiguedad)} años`, 
              highlight: false 
            }
          ]}
        />
      </div>

      {/* Detalles Adicionales - Solo visible cuando showAll es true */}
      {showAll && (
        <div className="mb-10">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-white/20 pb-2 mb-6">
            Detalles Adicionales
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Medidas */}
            <FeatureCard 
              icon={Ruler} 
              title="MEDIDAS GENERALES"
              color="purple"
              values={[
                { 
                  label: "Largo", 
                  value: `${getPropertyValue(property.largo_terreno)} m`, 
                  highlight: false 
                },
                { 
                  label: "Ancho", 
                  value: `${getPropertyValue(property.ancho_terreno)} m`, 
                  highlight: false 
                },
                { 
                  label: "Área Total", 
                  value: `${getPropertyValue(property.area_total)} m²`, 
                  highlight: true 
                }
              ]}
            />
            
            {/* Habitaciones */}
            <FeatureCard 
              icon={Bed} 
              title="DETALLE HABITACIONES"
              color="rose"
              values={[
                { 
                  label: "Habitaciones", 
                  value: getPropertyValue(property.habitaciones), 
                  highlight: false 
                },
                { 
                  label: "Baños", 
                  value: getPropertyValue(property.banos), 
                  highlight: false 
                },
                { 
                  label: "Habitación principal", 
                  value: "Con baño", 
                  highlight: false 
                }
              ]}
            />
            
            {/* Estructura */}
            <FeatureCard 
              icon={Layout} 
              title="ESTRUCTURA"
              color="gray"
              values={[
                { 
                  label: "Pisos", 
                  value: getPropertyValue(property.pisos), 
                  highlight: false 
                },
                { 
                  label: "Antigüedad", 
                  value: `${getPropertyValue(property.antiguedad)} años`, 
                  highlight: false 
                },
                { 
                  label: "Tipo", 
                  value: getPropertyValue(property.tipo, 'Estándar'), 
                  highlight: false 
                }
              ]}
            />
          </div>
        </div>
      )}
        
      {/* Servicios - SIEMPRE VISIBLES */}
      <div className="mb-10">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-white/20 pb-2 mb-6">
          Servicios Disponibles
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <BooleanFeature 
            title="Agua" 
            isAvailable={checkService('agua')} 
            icon={Droplet}
            color="blue"
          />
          
          <BooleanFeature 
            title="Luz" 
            isAvailable={checkService('luz')} 
            icon={Zap}
            color="amber"
          />
          
          <BooleanFeature 
            title="Internet" 
            isAvailable={checkService('internet')} 
            icon={Wifi}
            color="emerald"
          />
          
          <BooleanFeature 
            title="Estacionamiento" 
            isAvailable={property.estacionamiento === 'Si'} 
            icon={Car}
            color="blue"
          />
          
          <BooleanFeature 
            title="Áreas Verdes" 
            isAvailable={property.areas_verdes === 'Si'} 
            icon={TreePine}
            color="emerald"
          />
          
          <BooleanFeature 
            title="Seguridad" 
            isAvailable={checkService('seguridad')} 
            icon={Shield}
            color="gray"
          />
        </div>
      </div>

      {/* Toggle button */}
      <div className="flex justify-center mt-8">
        <button
          onClick={toggleShowAll}
          className="flex items-center gap-2 px-8 py-3 
                   bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-400 dark:to-blue-500
                   text-white rounded-xl shadow-md dark:shadow-black/30
                   transition-all duration-300 
                   hover:shadow-lg dark:hover:shadow-black/50 hover:scale-105 active:scale-95"
        >
          {showAll ? (
            <>
              <Minimize2 className="w-5 h-5" />
              <span>Ver menos</span>
            </>
          ) : (
            <>
              <Maximize2 className="w-5 h-5" />
              <span>Ver más detalles</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CharacteristicsSection;